import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import useAnalytics from './hooks/useAnalytics';
import ProtectedRoute from './components/ProtectedRoute';
import FactoryOSLanding from './FactoryOSLanding';
import MachiningProgram from './MachiningProgram';
import AccessRequestsAdmin from './AccessRequestsAdmin';
import LoginPage from './LoginPage';
import DOEPlatformHome from './DOEPlatformHome';
import ExperimentDashboard from './ExperimentDashboard';
import ExperimentWizard from './ExperimentWizard';
import DesignMatrixPage from './DesignMatrixPage';
import AnalysisResultsPage from './AnalysisResultsPage';
import ConfirmationRunPage from './ConfirmationRunPage';
import ResponseSurfacePage from './ResponseSurfacePage';
import ProcessControlPage from './ProcessControlPage';
import HelpCenter from './HelpCenter';
// Engineering Design Cycle
import DesignCycleHome from './DesignCycleHome';
import DesignCycleWizard from './DesignCycleWizard';
import DesignCycleNewProject from './DesignCycleNewProject';
import DesignReviewsPage from './components/design-cycle/DesignReviewsPage';
// Quality (8D)
import QualityDashboard from './components/quality/QualityDashboard';
import QualityCaseDetail from './components/quality/QualityCaseDetail';
// Timeline (Gantt)
import TimelinePage from './components/timeline/TimelinePage';
// SOPs (Alpha)
import { SOPListPage, SOPDetailPage } from './components/sops';
// Reporting
import ReportingDashboard from './components/reporting/ReportingDashboard';
import Baja2025DesignReport from './components/reporting/Baja2025DesignReport';
// Executive Dashboard (Corporate Only)
import ExecutiveDashboard from './components/executive/ExecutiveDashboard';
// Resources (Tool & Asset Inventory)
import { ResourcesHome } from './components/resources';
// Shared Experiment Access
import SharedExperimentPage from './pages/SharedExperimentPage';
import SharedWithMePage from './pages/SharedWithMePage';
// Legal & trust pages
import PrivacyPolicy from './pages/PrivacyPolicy';
import AccessibilityStatement from './pages/AccessibilityStatement';
import TermsOfUse from './pages/TermsOfUse';
import ContactPage from './pages/ContactPage';
// Notebook QR entry point
import StartPage from './pages/StartPage';
// Audience landing pages
import HardwareStartupsPage from './pages/audiences/HardwareStartupsPage';
import DefenseAerospacePage from './pages/audiences/DefenseAerospacePage';
import UniversityTeamsPage from './pages/audiences/UniversityTeamsPage';
// Marketing pages added in the site audit pass
import PricingPage from './pages/PricingPage';
import SecurityTrustPage from './pages/SecurityTrustPage';
import FAQPage from './pages/FAQPage';
import ResourcesIndexPage from './pages/ResourcesIndexPage';
// Pillar essays
import WhatIsEngineeringExecution from './pages/resources/WhatIsEngineeringExecution';
import DesignEvidenceAsByproduct from './pages/resources/DesignEvidenceAsByproduct';
import IadtVerificationForHardware from './pages/resources/IadtVerificationForHardware';
import EngineeringExecutionVsPlm from './pages/resources/EngineeringExecutionVsPlm';

/* Inside <Router> so it can see route changes; renders nothing. */
function Analytics() {
  useAnalytics();
  return null;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Analytics />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<FactoryOSLanding />} />
          <Route path="/notebook" element={<Navigate to="/" replace />} />
          <Route path="/cinematic" element={<Navigate to="/" replace />} />
          <Route path="/machining" element={<MachiningProgram />} />
          <Route path="/start" element={<StartPage />} />
          <Route path="/for/hardware-startups" element={<HardwareStartupsPage />} />
          <Route path="/for/defense-aerospace" element={<DefenseAerospacePage />} />
          <Route path="/for/university-teams" element={<UniversityTeamsPage />} />
          <Route path="/resources" element={<ResourcesIndexPage />} />
          <Route path="/resources/what-is-an-engineering-operating-system" element={<WhatIsEngineeringExecution />} />
          <Route path="/resources/design-evidence-as-byproduct" element={<DesignEvidenceAsByproduct />} />
          <Route path="/resources/iadt-verification-for-hardware-teams" element={<IadtVerificationForHardware />} />
          <Route path="/resources/engineering-operating-system-vs-plm" element={<EngineeringExecutionVsPlm />} />
          {/* Old essay slugs — the category was renamed; keep shared links alive */}
          <Route path="/resources/what-is-engineering-execution" element={<Navigate to="/resources/what-is-an-engineering-operating-system" replace />} />
          <Route path="/resources/engineering-execution-vs-plm" element={<Navigate to="/resources/engineering-operating-system-vs-plm" replace />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/security" element={<SecurityTrustPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/accessibility" element={<AccessibilityStatement />} />
          <Route path="/terms" element={<TermsOfUse />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/admin/requests" element={<AccessRequestsAdmin />} />
          <Route path="/login" element={<LoginPage />} />

          {/* Shared Experiment Access (requires login) */}
          <Route path="/share/experiment/:token" element={
            <ProtectedRoute>
              <SharedExperimentPage />
            </ProtectedRoute>
          } />
          <Route path="/shared-with-me" element={
            <ProtectedRoute>
              <SharedWithMePage />
            </ProtectedRoute>
          } />

          {/* Protected DOE Platform Routes */}
          <Route path="/doe" element={
            <ProtectedRoute>
              <DOEPlatformHome />
            </ProtectedRoute>
          } />
          <Route path="/doe/dashboard" element={
            <ProtectedRoute>
              <ExperimentDashboard />
            </ProtectedRoute>
          } />
          <Route path="/doe/new" element={
            <ProtectedRoute>
              <ExperimentWizard />
            </ProtectedRoute>
          } />
          <Route path="/doe/experiment/:id" element={
            <ProtectedRoute>
              <DesignMatrixPage />
            </ProtectedRoute>
          } />
          <Route path="/doe/analysis/:id" element={
            <ProtectedRoute>
              <AnalysisResultsPage />
            </ProtectedRoute>
          } />
          <Route path="/doe/confirmation" element={
            <ProtectedRoute>
              <ConfirmationRunPage />
            </ProtectedRoute>
          } />
          <Route path="/doe/response-surface" element={
            <ProtectedRoute>
              <ResponseSurfacePage />
            </ProtectedRoute>
          } />
          <Route path="/doe/process-control" element={
            <ProtectedRoute>
              <ProcessControlPage />
            </ProtectedRoute>
          } />
          <Route path="/doe/help" element={
            <ProtectedRoute>
              <HelpCenter />
            </ProtectedRoute>
          } />

          {/* Protected Engineering Design Cycle Routes */}
          <Route path="/design" element={
            <ProtectedRoute>
              <DesignCycleHome />
            </ProtectedRoute>
          } />
          <Route path="/design/new" element={
            <ProtectedRoute>
              <DesignCycleNewProject />
            </ProtectedRoute>
          } />
          <Route path="/design/project/:id" element={
            <ProtectedRoute>
              <DesignCycleWizard />
            </ProtectedRoute>
          } />
          <Route path="/design/reviews" element={
            <ProtectedRoute>
              <DesignReviewsPage />
            </ProtectedRoute>
          } />

          {/* Protected Quality (8D) Routes */}
          <Route path="/quality" element={
            <ProtectedRoute>
              <QualityDashboard />
            </ProtectedRoute>
          } />
          <Route path="/quality/:id" element={
            <ProtectedRoute>
              <QualityCaseDetail />
            </ProtectedRoute>
          } />

          {/* Protected Timeline (Gantt) Routes */}
          <Route path="/timeline" element={
            <ProtectedRoute>
              <TimelinePage />
            </ProtectedRoute>
          } />

          {/* Protected SOPs (Alpha) Routes */}
          <Route path="/sops" element={
            <ProtectedRoute>
              <SOPListPage />
            </ProtectedRoute>
          } />
          <Route path="/sops/:id" element={
            <ProtectedRoute>
              <SOPDetailPage />
            </ProtectedRoute>
          } />

          {/* Protected Reporting Routes */}
          <Route path="/reporting" element={
            <ProtectedRoute>
              <ReportingDashboard />
            </ProtectedRoute>
          } />
          <Route path="/reporting/baja-2025-design-report" element={
            <ProtectedRoute>
              <Baja2025DesignReport />
            </ProtectedRoute>
          } />

          {/* Protected Executive Dashboard Routes (Corporate Only) */}
          <Route path="/executive" element={
            <ProtectedRoute>
              <ExecutiveDashboard />
            </ProtectedRoute>
          } />

          {/* Protected Resources (Tool & Asset Inventory) Routes.
              Lives at /app/resources so the public essay index owns /resources. */}
          <Route path="/app/resources" element={
            <ProtectedRoute>
              <ResourcesHome />
            </ProtectedRoute>
          } />

          {/* Redirect any unknown routes to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
